import React, { useEffect, useState } from "react";
import { useSyncStore } from "@/store/sync.store";
import { cn } from "@/utilities";
import { CheckCircle2, CloudUpload, Loader2, XCircle } from "lucide-react";

/**
 * SyncBubble - Widget Flotante de Sincronización
 *
 * Burbuja minimizada que muestra el progreso de sincronización
 * mientras el usuario continúa trabajando en otras áreas.
 *
 * Características:
 * - Muestra porcentaje de progreso
 * - Click para maximizar el modal
 * - Auto-hide después de completar
 * - Indicador visual de estado (éxito/error)
 */
export const SyncBubble: React.FC = () => {
  const [isHiding, setIsHiding] = useState(false);

  const isVisible = useSyncStore((state) => state.isVisible);
  const isMinimized = useSyncStore((state) => state.isMinimized);
  const successCount = useSyncStore((state) => state.successCount);
  const errorCount = useSyncStore((state) => state.errorCount);
  const statusMessage = useSyncStore((state) => state.statusMessage);

  const maximize = useSyncStore((state) => state.maximize);
  const close = useSyncStore((state) => state.close);
  const getProgress = useSyncStore((state) => state.getProgress);
  const isComplete = useSyncStore((state) => state.isComplete);

  const progress = getProgress();
  const complete = isComplete();

  // Auto-hide después de completar
  useEffect(() => {
    if (complete && isMinimized) {
      setIsHiding(false);
      
      // Timer principal para iniciar la animación de ocultar
      const hideTimer = setTimeout(() => {
        setIsHiding(true);
      }, 4000);
      
      // Timer secundario para cerrar después de la animación
      const closeTimer = setTimeout(() => {
        close();
      }, 4500); // 4000ms de espera + 500ms de animación
      
      return () => {
        clearTimeout(hideTimer);
        clearTimeout(closeTimer);
      };
    }
  }, [complete, isMinimized, close]);

  // Solo mostrar si está visible Y minimizado
  if (!isVisible || !isMinimized) return null;

  /**
   * Determina el color del borde según el estado
   */
  const getBorderColor = () => {
    if (!complete) return "border-primary";
    if (errorCount > 0) return "border-yellow-500";
    return "border-green-500";
  };

  /**
   * Determina el icono según el estado
   */
  const getIcon = () => {
    if (!complete) {
      return <Loader2 className="h-5 w-5 text-primary animate-spin" />;
    }
    if (errorCount > 0) {
      return <XCircle className="h-5 w-5 text-yellow-500" />;
    }
    return <CheckCircle2 className="h-5 w-5 text-green-500" />;
  };

  /**
   * Texto de estado resumido
   */
  const getStatusText = () => {
    if (!complete) {
      return `${progress}%`;
    }
    if (errorCount > 0) {
      return `${successCount}✓ ${errorCount}✗`;
    }
    return "¡Listo!";
  };

  return (
    <div
      className={cn(
        "fixed bottom-4 right-4 z-50 transition-all duration-300 ease-out",
        isHiding && "opacity-0 translate-y-4"
      )}
    >
      <button
        onClick={maximize}
        className={cn(
          "flex items-center gap-3 bg-card border-2 rounded-full px-4 py-2 shadow-lg",
          "hover:shadow-xl hover:scale-105 transition-all cursor-pointer",
          getBorderColor()
        )}
        title={statusMessage}
      >
        {/* Icono con indicador de progreso circular */}
        <div className="relative">
          {getIcon()}
          {!complete && (
            <svg
              className="absolute -inset-1 w-7 h-7"
              viewBox="0 0 28 28"
            >
              <circle
                cx="14"
                cy="14"
                r="12"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                className="text-muted/30"
              />
              <circle
                cx="14"
                cy="14"
                r="12"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                className="text-primary"
                strokeDasharray={`${(progress / 100) * 75.4} 75.4`}
                transform="rotate(-90 14 14)"
              />
            </svg>
          )}
        </div>

        {/* Texto de estado */}
        <div className="flex flex-col items-start">
          <span className="text-sm font-medium">{getStatusText()}</span>
          <span className="text-[10px] text-muted-foreground flex items-center gap-1">
            <CloudUpload className="h-3 w-3" />
            Sincronización
          </span>
        </div>
      </button>

      {/* Tooltip con mensaje completo */}
      {complete && (
        <div
          className={cn(
            "absolute bottom-full right-0 mb-2 px-3 py-2 bg-card border rounded-lg shadow-lg",
            "text-sm max-w-[200px] text-center",
            "animate-in fade-in slide-in-from-bottom-2 duration-300"
          )}
        >
          {errorCount > 0 ? (
            <>
              <p className="font-medium text-yellow-600">Sincronización parcial</p>
              <p className="text-xs text-muted-foreground">
                {successCount} exitosos, {errorCount} con error
              </p>
            </>
          ) : (
            <>
              <p className="font-medium text-green-600">¡Sincronización exitosa!</p>
              <p className="text-xs text-muted-foreground">
                {successCount} registros sincronizados
              </p>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default SyncBubble;
